import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * MomentComments — Threaded reply UI for a single moment.
 *
 * Rendered inside the moment detail modal on the Admin, Staff and Parent
 * dashboards. Both parents and staff can post; author or moderator
 * (admin / org_owner / org_admin / deputy_manager / room_leader) can
 * edit / delete.
 *
 * Props
 *   momentId    — the moment being commented on
 *   currentUser — the logged-in user (needs `_id` and `role`)
 *   onCountChange — optional callback(newCount) so parents can update
 *                   the card's badge without a full refetch
 *   compact     — when true, hides the section header (used inside a
 *                 dialog where the moment header already says "Comments")
 */
const MODERATOR_ROLES = new Set([
  'admin', 'org_owner', 'org_admin', 'deputy_manager', 'room_leader',
]);

const roleBadgeStyle = (role) => {
  if (!role) return null;
  if (role === 'parent') {
    return { background: 'hsl(220 70% 94%)', color: 'hsl(220 60% 30%)' };
  }
  if (MODERATOR_ROLES.has(role) || role === 'staff') {
    return { background: 'hsl(150 60% 92%)', color: 'hsl(150 45% 26%)' };
  }
  return { background: 'hsl(220 15% 92%)', color: 'hsl(220 15% 35%)' };
};

const prettyRole = (role) => {
  if (!role) return '';
  return String(role).replace(/_/g, ' ');
};

export function MomentComments({ momentId, currentUser, onCountChange, compact = false }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingBody, setEditingBody] = useState('');
  const listEndRef = useRef(null);

  const notifyCount = useCallback((next) => {
    if (typeof onCountChange === 'function') onCountChange(next);
  }, [onCountChange]);

  const load = useCallback(async () => {
    if (!momentId) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.getMomentComments(momentId);
      setComments(Array.isArray(data) ? data : []);
      notifyCount(Array.isArray(data) ? data.length : 0);
    } catch (err) {
      setError(err?.message || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [momentId, notifyCount]);

  useEffect(() => { load(); }, [load]);

  // Auto-scroll to the newest comment when the list grows.
  useEffect(() => {
    if (!loading && comments.length > 0 && listEndRef.current) {
      try { listEndRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch { /* noop */ }
    }
  }, [comments.length, loading]);

  const handlePost = async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    if (!body) return;
    setSubmitting(true);
    try {
      const created = await api.createMomentComment(momentId, body);
      setComments((prev) => {
        const next = [...prev, created];
        notifyCount(next.length);
        return next;
      });
      setDraft('');
    } catch (err) {
      toast.error(err?.message || 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c._id);
    setEditingBody(c.body || '');
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingBody('');
  };
  const saveEdit = async () => {
    const body = editingBody.trim();
    if (!body) return;
    setSubmitting(true);
    try {
      const updated = await api.updateMomentComment(momentId, editingId, body);
      setComments((prev) => prev.map((c) => (c._id === updated._id ? updated : c)));
      cancelEdit();
    } catch (err) {
      toast.error(err?.message || 'Failed to update comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm('Delete this comment? This cannot be undone.')) return;
    setSubmitting(true);
    try {
      await api.deleteMomentComment(momentId, c._id);
      setComments((prev) => {
        const next = prev.filter((x) => x._id !== c._id);
        notifyCount(next.length);
        return next;
      });
    } catch (err) {
      toast.error(err?.message || 'Failed to delete comment');
    } finally {
      setSubmitting(false);
    }
  };

  const canModifyComment = (c) => {
    if (!currentUser) return false;
    if (currentUser._id === c.author_id) return true;
    return MODERATOR_ROLES.has(currentUser.role);
  };

  return (
    <div data-testid="moment-comments" style={{ marginTop: '0.75rem' }}>
      {!compact && (
        <h3 style={{ margin: '0.5rem 0', fontSize: '0.95rem' }}>
          💬 Replies {comments.length > 0 && (
            <span style={{ color: 'hsl(var(--muted-foreground))', fontWeight: 400 }}>
              ({comments.length})
            </span>
          )}
        </h3>
      )}

      {error && (
        <div
          className="alert alert-error"
          data-testid="moment-comments-error"
          style={{
            background: 'hsl(0 70% 95%)', color: 'hsl(0 70% 35%)',
            padding: '0.5rem 0.75rem', borderRadius: 6,
            marginBottom: '0.5rem', fontSize: '0.85rem',
          }}
        >
          {error}
        </div>
      )}

      {/* Thread */}
      <div
        style={{
          maxHeight: 260,
          overflowY: 'auto',
          border: '1px solid hsl(var(--border))',
          borderRadius: 'var(--radius)',
          padding: '0.5rem',
          background: 'hsl(var(--muted) / 0.2)',
        }}
        data-testid="moment-comments-thread"
      >
        {loading ? (
          <div style={{ padding: '0.5rem', fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }} data-testid="moment-comments-loading">
            Loading replies…
          </div>
        ) : comments.length === 0 ? (
          <div
            style={{ padding: '0.5rem', fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', textAlign: 'center' }}
            data-testid="moment-comments-empty"
          >
            No replies yet. Be the first to leave a note.
          </div>
        ) : (
          comments.map((c) => {
            const isEditing = editingId === c._id;
            const canMod = canModifyComment(c);
            const badge = roleBadgeStyle(c.author_role_snapshot || c.author_role);
            return (
              <div
                key={c._id}
                data-testid={`moment-comment-${c._id}`}
                style={{
                  padding: '0.5rem 0.6rem',
                  marginBottom: '0.35rem',
                  background: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 6,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem', flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: '0.85rem' }} data-testid={`moment-comment-author-${c._id}`}>
                    {c.author_name || 'Unknown'}
                  </strong>
                  {badge && (
                    <span
                      className="badge"
                      style={{
                        ...badge,
                        fontSize: '0.68rem',
                        padding: '0.05rem 0.4rem',
                        borderRadius: 999,
                        fontWeight: 600,
                        textTransform: 'capitalize',
                      }}
                    >
                      {prettyRole(c.author_role_snapshot || c.author_role)}
                    </span>
                  )}
                  <small style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.7rem' }}>
                    {new Date(c.created_at).toLocaleString()}
                    {c.edited_at && ' • edited'}
                  </small>
                </div>
                {isEditing ? (
                  <>
                    <textarea
                      value={editingBody}
                      onChange={(e) => setEditingBody(e.target.value)}
                      rows={2}
                      maxLength={1500}
                      data-testid={`moment-comment-edit-textarea-${c._id}`}
                      style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: '0.9rem' }}
                    />
                    <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.35rem', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={cancelEdit}
                        disabled={submitting}
                        data-testid={`moment-comment-edit-cancel-${c._id}`}
                        style={{ fontSize: '0.8rem' }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={saveEdit}
                        disabled={submitting || !editingBody.trim()}
                        data-testid={`moment-comment-edit-save-${c._id}`}
                        style={{ fontSize: '0.8rem' }}
                      >
                        Save
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p
                      style={{ margin: '0.1rem 0 0.2rem', whiteSpace: 'pre-wrap', fontSize: '0.9rem', lineHeight: 1.4 }}
                      data-testid={`moment-comment-body-${c._id}`}
                    >
                      {c.body}
                    </p>
                    {canMod && (
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.2rem' }}>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => startEdit(c)}
                          disabled={submitting}
                          data-testid={`moment-comment-edit-${c._id}`}
                          style={{ fontSize: '0.72rem', padding: '0.15rem 0.4rem' }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => handleDelete(c)}
                          disabled={submitting}
                          data-testid={`moment-comment-delete-${c._id}`}
                          style={{ fontSize: '0.72rem', padding: '0.15rem 0.4rem', color: 'hsl(0 60% 40%)' }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })
        )}
        <div ref={listEndRef} />
      </div>

      {/* Composer */}
      <form onSubmit={handlePost} style={{ marginTop: '0.5rem' }}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          maxLength={1500}
          placeholder="Write a reply…"
          data-testid="moment-comments-textarea"
          disabled={submitting}
          style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: '0.9rem' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.35rem' }}>
          <small style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.7rem' }}>
            {draft.length}/1500
          </small>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || !draft.trim()}
            data-testid="moment-comments-submit"
            style={{ fontSize: '0.85rem' }}
          >
            {submitting ? 'Posting…' : 'Post reply'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default MomentComments;
